import { useEffect, useMemo, useState } from "react"
import { useLocalization } from "@fluent/react"
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material"
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined"
import SearchIcon from "@mui/icons-material/Search"
import DOMPurify from "dompurify"
import { APIError, unknownError } from "../../../app/appTypes"
import { CancelButton } from "../../../app/components/CancelButton"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { selectSettings } from "../../settings/settingsSlice"
import {
    loadOperationalDashboardAreaMeetingAgendaItemHistory,
    loadOperationalDashboardMeetingItemHistory,
    OperationalDashboardMeetingItemHistoryDto,
} from "../operationalDashboards"

export type OperationalDashboardHistoryTarget =
    | { kind: "content"; contentId: number; name: string }
    | { kind: "agenda"; areaId: number; name: string }

export const OperationalDashboardHistoryDialog = ({ target, dashboardId, onClose }: {
    target: OperationalDashboardHistoryTarget
    dashboardId: number
    onClose: () => void
}) => {
    const dispatch = useAppDispatch()
    const { locale } = useAppSelector(selectSettings)
    const { l10n } = useLocalization()
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)
    const [history, setHistory] = useState<OperationalDashboardMeetingItemHistoryDto[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<APIError | null>(null)
    const [retry, setRetry] = useState(0)
    const [search, setSearch] = useState("")

    useEffect(() => {
        let active = true
        setLoading(true)
        setError(null)
        const request = target.kind === "content"
            ? dispatch(loadOperationalDashboardMeetingItemHistory({ dashboardId, areaContentId: target.contentId }))
            : dispatch(loadOperationalDashboardAreaMeetingAgendaItemHistory({ dashboardId, areaId: target.areaId }))
        request.then(result => {
            if (!active) return
            if (loadOperationalDashboardMeetingItemHistory.fulfilled.match(result) ||
                loadOperationalDashboardAreaMeetingAgendaItemHistory.fulfilled.match(result)) {
                setHistory(result.payload)
            } else {
                setError(loadOperationalDashboardMeetingItemHistory.rejected.match(result) ||
                    loadOperationalDashboardAreaMeetingAgendaItemHistory.rejected.match(result)
                    ? result.payload ?? unknownError() : unknownError())
            }
            setLoading(false)
        })
        return () => { active = false }
    }, [dispatch, dashboardId, target, retry])

    const dateLocale = locale === "pl" ? "pl-PL" : locale === "de" ? "de-DE" : "en-US"
    const formatDate = (value: string) => {
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? label("meeting-date-invalid", "Nieprawidłowa data")
            : new Intl.DateTimeFormat(dateLocale, {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
            }).format(date)
    }
    const entries = useMemo(() => [...history]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .map(entry => {
            const html = DOMPurify.sanitize(entry.content ?? "", {
                ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "s", "ul", "ol", "li", "blockquote", "a", "div", "span"],
                ALLOWED_ATTR: ["href", "title"],
            })
            return { entry, html, text: new DOMParser().parseFromString(html, "text/html").body.textContent ?? "" }
        }), [history])
    const query = search.trim().toLocaleLowerCase()
    const visibleEntries = query ? entries.filter(({ entry, text }) =>
        [entry.user, entry.date, formatDate(entry.date), text].join(" ").toLocaleLowerCase().includes(query)) : entries

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="operational-dashboard-history-title">
            <DialogTitle id="operational-dashboard-history-title" sx={{ bgcolor: "grey.200", color: "text.primary" }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <HistoryOutlinedIcon color="primary" />
                    <Typography component="span" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
                        {l10n.getString("history", null, "_Historia")}: {target.kind === "agenda"
                            ? `${label("decision-action", "Decyzja / akcja")} - ${target.name}` : target.name}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ minHeight: 130 }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
                ) : error ? (
                    <Alert severity="error" action={
                        <Button color="inherit" size="small" onClick={() => setRetry(value => value + 1)}>
                            {l10n.getString("retry", null, "_Ponów")}
                        </Button>
                    }>
                        {error.kind === "http" && error.problem?.title
                            ? l10n.getString(error.problem.title, null, `_${error.problem.detail || "Nie udało się pobrać historii."}`)
                            : label("history-load-error", "Nie udało się pobrać historii.")}
                    </Alert>
                ) : entries.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                        {label("history-empty", "Brak wpisów historii dla tej treści.")}
                    </Typography>
                ) : (
                    <>
                        <TextField size="small" fullWidth value={search} sx={{ mt: 1, mb: 2 }}
                            label={label("history-search", "Szukaj w historii")}
                            onChange={event => setSearch(event.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                        <TableContainer component={Paper} sx={{ maxHeight: 520, border: 1, borderColor: "divider", borderRadius: 2 }}>
                            <Table stickyHeader size="small" sx={{ minWidth: 600, tableLayout: "fixed" }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 155, fontWeight: 700 }}>{l10n.getString("date", null, "_Data")}</TableCell>
                                        <TableCell sx={{ width: 160, fontWeight: 700 }}>{l10n.getString("user", null, "_Użytkownik")}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{l10n.getString("content", null, "_Treść")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleEntries.length ? visibleEntries.map(({ entry, html }, index) => (
                                        <TableRow key={`${entry.date}-${index}`} hover>
                                            <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>{formatDate(entry.date)}</TableCell>
                                            <TableCell sx={{ verticalAlign: "top", overflowWrap: "anywhere" }}>
                                                {entry.user || l10n.getString("unknown-user", null, "_Nieznany użytkownik")}
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: "top", overflowWrap: "anywhere" }}>
                                                {html ? (
                                                    <Box sx={{ whiteSpace: "pre-wrap",
                                                        "& p": { my: 0.5 }, "& p:first-child": { mt: 0 }, "& p:last-child": { mb: 0 },
                                                        "& ul, & ol": { pl: 3, my: 0.75 },
                                                    }} dangerouslySetInnerHTML={{ __html: html }} />
                                                ) : ""}
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                                {label("history-no-matches", "Brak wpisów pasujących do wyszukiwania.")}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <CancelButton loading={false} onClick={onClose} label={l10n.getString("close", null, "_Zamknij")} />
            </DialogActions>
        </Dialog>
    )
}
